import React, { useContext, useState, useEffect } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';

import Headline from '@components/atoms/headline/headline';
import Copy from '@components/atoms/copy/copy';
import MixedCopy from '@components/molecules/mixed-copy/mixedCopy';

import formDe from '../../data/wish-dialog-de';
import formEn from '../../data/wish-dialog-en';

import Popover from '../popover/popover';
import WishRadio from '../wishRadio/wishRadio';
import WishLocation from '../wishLocation/wishLocation';
import { EvaluationIcon } from '../icons/icons';
import { WishesContext } from '../../contexts/wishesProvider';

import { generateSpherePoints } from '../../helpers/generateSpherePoints';

import EvaluationCategories from './components/evaluationCategories';
import EvaluationWishes from './components/evaluationWishes';
import EvaluationPersonal from './components/evaluationPersonal';
import StyledEvaluation from './evaluation.style';

const Evaluation = () => {
  const t = useTranslations();
  const locale = useLocale();
  const filterData = locale === 'de' ? formDe : formEn;
  const {
    categories,
    wishes,
    personalWish,
    wishesApiKeys,
    introduction,
    evaluationIntro,
    openEvaluation,
    setOpenEvaluation
  } = useContext(WishesContext);
  const [googleMapsPlacesLoaded, setGoogleMapsPlacesLoaded] = useState(null);
  const [topCategories, setTopCategories] = useState([]);
  const [topWishes, setTopWishes] = useState([]);
  const [filter, setFilter] = useState(undefined);
  const [locationOutput, setLocationOutput] = useState('');
  const [updatedWishes, setUpdatedWishes] = useState(null);

  const handleChangeSelect = input => inputValue => {
    if (googleMapsPlacesLoaded) {
      setLocationOutput(inputValue);
      setFilter({ name: input, value: inputValue?.label });
    }
  };

  useEffect(() => {
    setUpdatedWishes(generateSpherePoints(wishes, 0));
  }, []);

  useEffect(() => {
    if (!updatedWishes) return;
    let maxWishes = 0;
    updatedWishes.forEach(wish => {
      if (filter && wish[filter.name] !== filter.value) {
        return;
      }

      maxWishes += wish.wishedCount;
    });

    const wished = updatedWishes
      .filter(i => {
        if (!filter) return true;
        return i[filter.name] === filter.value;
      })
      .reduce((prev, wish) => {
        let obj = prev;

        wish.categories.forEach(category => {
          obj = { ...obj, [category]: (obj[category] ?? 0) + wish.wishedCount };
        });

        return obj;
      }, {});

    const sortedCategories = categories
      .filter(c => c.id in wished)
      .sort((a, b) => wished[b.id] - wished[a.id]);

    const top5Categories = sortedCategories
      .map(i => ({ ...i, maxWishes, wishCount: wished[i.id] }))
      .slice(0, 5);
    setTopCategories(top5Categories);

    const sortedWishes = updatedWishes.sort((a, b) => b.wishedCount - a.wishedCount);
    const top5Wishes = sortedWishes
      .filter(i => {
        if (i.baseId) return null;

        if (filter) {
          if (i[filter.name] === filter.value) {
            return i;
          }

          return null;
        }

        return i;
      })
      .slice(0, 5);
    setTopWishes(top5Wishes);
  }, [updatedWishes, filter]);

  return (
    <StyledEvaluation>
      <Popover
        headline={
          <>
            <span className="not-mobile">{t('wishes.evaluation.headline')}</span>

            <span className="not-desktop">
              {t(
                personalWish ? 'wishes.evaluation.personalHeadline' : 'wishes.evaluation.headline'
              )}
            </span>
          </>
        }
        placement="top-start"
        offsetX={100}
        closeLabel={t('close')}
        trigger={{
          label: t('wishes.evaluation.label'),
          ariaLabel: t('wishes.evaluation.label'),
          icon: <EvaluationIcon />
        }}
        open={openEvaluation}
        onOpen={setOpenEvaluation}
      >
        {personalWish && (
          <EvaluationPersonal
            headline={t('wishes.evaluation.personalHeadline')}
            wish={personalWish}
            categories={categories}
          />
        )}
        <div className="evaluation__main">
          {personalWish && (
            <Headline tag="h2" main className="not-desktop">
              {t('wishes.evaluation.headline')}
            </Headline>
          )}
          <Tabs onSelect={index => (index === 0 ? setFilter(undefined) : '')}>
            <TabList>
              <Tab>{t('wishes.evaluation.filter.total')}</Tab>
              <Tab>{t('wishes.evaluation.filter.age')}</Tab>
              <Tab>{t('wishes.evaluation.filter.gender')}</Tab>
              <Tab>{t('wishes.evaluation.filter.location')}</Tab>
            </TabList>

            <TabPanel>
              <MixedCopy
                className="evaluation__total-text"
                html={evaluationIntro || introduction?.introCopy}
              />
            </TabPanel>
            <TabPanel>
              <fieldset>
                <Copy tag="legend" extraSmall noMargin>
                  {filterData.age.label}
                </Copy>
                {filterData.age.cells.map((item, index) => (
                  <WishRadio
                    key={index}
                    label={item.label}
                    name="evaluationFilter"
                    value={item.value}
                    onChange={() => setFilter({ name: item.name, value: item.value })}
                  />
                ))}
              </fieldset>
            </TabPanel>
            <TabPanel>
              <fieldset>
                <Copy tag="legend" extraSmall noMargin>
                  {filterData.gender.label}
                </Copy>

                {filterData.gender.cells.map((item, index) => (
                  <WishRadio
                    key={index}
                    label={item.label}
                    name="evaluationFilter"
                    value={item.value}
                    onChange={() => setFilter({ name: item.name, value: item.value })}
                  />
                ))}
              </fieldset>
            </TabPanel>
            <TabPanel>
              <fieldset>
                <Copy tag="legend" extraSmall noMargin>
                  {filterData.location.label}
                </Copy>
                <WishLocation
                  googleMapsKey={
                    wishesApiKeys?.googleMapsApiKey ||
                    process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ||
                    ''
                  }
                  countryRestriction={[]}
                  onSDKLoaded={setGoogleMapsPlacesLoaded}
                  inputProps={{
                    id: filterData.location.id,
                    label: filterData.location.label,
                    name: filterData.location.name,
                    placeholder: filterData.location.placeholder,
                    value: locationOutput || '',
                    onChange: handleChangeSelect(filterData.location.name)
                  }}
                  labels={filterData.location.labels}
                />
              </fieldset>
            </TabPanel>
          </Tabs>

          <Copy
            extraSmall
            dangerouslySetInnerHTML={{
              __html: t('wishes.evaluation.topCategories')
            }}
          />
          <EvaluationCategories items={topCategories} locale={locale} />
          <Copy extraSmall>{t('wishes.evaluation.topWishes')}</Copy>
          <EvaluationWishes items={topWishes} locale={locale} />
        </div>
      </Popover>
    </StyledEvaluation>
  );
};

export default Evaluation;
